// NudgeCard: renders a single Nudge from useNudges.
// Dark glass-card aesthetic consistent with existing VYV card components
// (see src/index.css: .glass-card + src/components/onboarding/AvatarProviderCard).

import { motion } from "framer-motion";
import { Sparkles, Heart, Moon, Target, X } from "lucide-react";
import type { Nudge, NudgeType, NudgeVariant } from "@/hooks/useNudges";

interface NudgeCardProps {
  nudge: Nudge;
  onDismiss: () => void;
  onAct: () => void;
  className?: string;
}

const NUDGE_ICONS: Record<NudgeType, typeof Sparkles> = {
  teach_mirror_incomplete: Sparkles,
  checkin_missed_today: Heart,
  sleep_not_logged_3days: Moon,
  goals_empty: Target,
};

// Border + icon accent per variant. Kept in HSL to match the tokens declared in
// src/index.css so the card reacts correctly to theme changes.
const VARIANT_STYLE: Record<
  NudgeVariant,
  { border: string; accent: string; ring: string }
> = {
  info: {
    border: "hsl(187 72% 50% / 0.30)",
    accent: "hsl(187 72% 50%)",
    ring: "hsl(187 72% 50% / 0.15)",
  },
  warning: {
    border: "hsl(38 92% 55% / 0.30)",
    accent: "hsl(38 92% 60%)",
    ring: "hsl(38 92% 55% / 0.15)",
  },
  gentle: {
    border: "hsl(222 18% 35% / 0.40)",
    accent: "hsl(210 20% 80%)",
    ring: "hsl(222 18% 30% / 0.15)",
  },
};

const NudgeCard = ({ nudge, onDismiss, onAct, className }: NudgeCardProps) => {
  const Icon = NUDGE_ICONS[nudge.type] ?? Sparkles;
  const style = VARIANT_STYLE[nudge.variant];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
      className={`glass-card p-5 rounded-2xl relative overflow-hidden ${className ?? ""}`}
      style={{ border: `1px solid ${style.border}` }}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
        style={{ background: "hsl(222 18% 20% / 0.40)" }}
      >
        <X size={12} className="text-foreground/55" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: style.ring, border: `1px solid ${style.border}` }}
        >
          <Icon size={15} style={{ color: style.accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-[14px] font-semibold text-foreground leading-snug">
            {nudge.title}
          </p>
          <p className="text-muted-foreground text-[12px] mt-1 font-body leading-relaxed">
            {nudge.description}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onAct}
        className="mt-4 w-full rounded-xl py-2.5 font-display font-medium text-[12px] tracking-wide transition-all active:scale-[0.98]"
        style={{
          background: style.accent,
          color: "hsl(222 38% 6%)",
        }}
      >
        {nudge.ctaLabel}
      </button>
    </motion.div>
  );
};

export default NudgeCard;
